import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { HomeHero } from "./HomeHero";

// T65: homepage hero carousel. All slides share one grid cell, so the box is the tallest slide's height from the first
// paint (no CLS when slides change). Slide 1 keeps the LCP image; later slides mount their backgrounds only after load.
// Only the active slide's video plays. Autoplay stops for reduced motion, on hover / focus / hidden tab, or when paused.
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const BTN = "inline-flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white motion-reduce:transition-none";

function useWarm() {
  const [warm, setWarm] = useState(false);
  useEffect(() => {
    let t;
    const go = () => { t = setTimeout(() => setWarm(true), 2500); };
    if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
    return () => { clearTimeout(t); window.removeEventListener("load", go); };
  }, []);
  return [warm, setWarm];
}

export function HomeCarousel({ slides, settings, onCountdownEnd }) {
  const n = slides.length;
  const [i, setI] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [hold, setHold] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [warm, setWarm] = useWarm();
  const touch = useRef(null);
  const auto = settings?.autoplay !== false && !reduced();
  const playing = auto && !userPaused && !hold && !hidden;
  const go = useCallback((k) => { setWarm(true); setI((x) => (k + n) % n); }, [n, setWarm]);
  useEffect(() => { if (i >= n) setI(0); }, [i, n]);
  useEffect(() => {
    const v = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", v);
    return () => document.removeEventListener("visibilitychange", v);
  }, []);
  useEffect(() => {
    if (!playing) return undefined;
    const s = slides[i]?.duration_s || settings?.interval_s || 7;  // T65 fix: each slide's own duration
    const t = setTimeout(() => { setWarm(true); setI((x) => (x + 1) % n); }, Math.min(30, Math.max(3, s)) * 1000);
    return () => clearTimeout(t);
  }, [playing, i, n, slides, settings?.interval_s, setWarm]);
  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); go(i - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); go(i + 1); }
  };
  const name = (s, k) => s?.source?.name || `Slide ${k + 1}`;
  return (
    <section aria-roledescription="carousel" aria-label="Featured offers" data-testid="hero-carousel" data-active={i} data-duration={slides[i]?.duration_s} data-playing={playing ? "1" : "0"}
      onMouseEnter={() => setHold(true)} onMouseLeave={() => setHold(false)}
      onFocus={() => setHold(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHold(false); }}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const d = e.changedTouches[0].clientX - (touch.current ?? e.changedTouches[0].clientX); touch.current = null; if (Math.abs(d) > 40) go(d < 0 ? i + 1 : i - 1); }}>
      <div className="grid" aria-live={playing ? "off" : "polite"}>
        {slides.map((s, k) => (
          <div key={s.source?.id || k} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${n}: ${name(s, k)}`} aria-hidden={k !== i}
            ref={(el) => { if (el) el.inert = k !== i; }} data-testid={`hero-slide-${k}`} data-active={k === i ? "yes" : "no"}
            className={`[grid-area:1/1] transition-opacity duration-500 motion-reduce:transition-none ${k === i ? "z-10 opacity-100" : "pointer-events-none z-0 opacity-0 [transition-delay:500ms] motion-reduce:[transition-delay:0ms]"}`}>
            <HomeHero hero={s} active={k === i} deferBg={k > 0 && !warm} onCountdownEnd={k === i ? onCountdownEnd : undefined} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-2 bg-[#08142D] px-4 py-1.5 text-white" onKeyDown={onKey} data-testid="hero-car-controls">
        <button type="button" className={BTN} aria-label="Previous slide" onClick={() => go(i - 1)} data-testid="hero-car-prev"><ChevronLeft size={22} aria-hidden="true" /></button>
        <div className="flex items-center gap-1" role="group" aria-label="Choose a slide">
          {slides.map((s, k) => (
            <button key={k} type="button" aria-label={`Show slide ${k + 1}: ${name(s, k)}`} aria-current={k === i ? "true" : undefined} onClick={() => go(k)}
              className="inline-flex h-11 w-8 items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" data-testid={`hero-car-dot-${k}`}>
              <span className={`block h-2.5 rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none ${k === i ? "w-6 bg-white" : "w-2.5 bg-white/45"}`} />
            </button>))}
        </div>
        <button type="button" className={BTN} aria-label="Next slide" onClick={() => go(i + 1)} data-testid="hero-car-next"><ChevronRight size={22} aria-hidden="true" /></button>
        {auto && <button type="button" className={BTN} aria-label={userPaused ? "Play slide rotation" : "Pause slide rotation"} aria-pressed={userPaused}
          onClick={() => setUserPaused(!userPaused)} data-testid="hero-car-pause">{userPaused ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}</button>}
      </div>
    </section>
  );
}
